import { useState } from "react";
import { Link } from "react-router-dom";
import { MarkdownContent } from "@/components/content/MarkdownContent";
import { Button } from "@/components/ui/Button";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SafeImage } from "@/components/ui/SafeImage";
import { IconRenderer } from "@/components/icons/IconRenderer";
import { SEO } from "@/components/seo/SEO";
import { useAboutPage } from "@/features/about/hooks";
import { PublicEmpty, PublicError, PublicLoading } from "@/pages/public/PublicState";

export function AboutPage() {
  const about = useAboutPage();
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  if (about.isLoading) return <PublicLoading label="Loading about page..." />;
  if (about.isError) return <PublicError label="Unable to load about page." />;
  if (!about.data) return <PublicEmpty title="About page unavailable" description="About content has not been configured yet." />;

  const page = about.data;
  const ctaUrl = page.ctaButtonUrl || "/contact";

  return (
    <section className="mx-auto w-full max-w-7xl px-3 py-8 sm:px-6 sm:py-12 lg:px-8">
      <SEO title={page.pageTitle} description={page.subtitle || page.biography.slice(0, 160)} ogImage={page.profileImageUrl} />
      <CyberPanel>
        <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center lg:gap-8">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200 sm:text-sm sm:tracking-[0.28em]">About</p>
            <h1 className="mt-3 break-words font-display text-3xl font-bold uppercase text-white sm:text-5xl lg:text-6xl">{page.pageTitle}</h1>
            {page.subtitle ? <p className="mt-4 max-w-3xl text-base leading-7 text-content-secondary sm:mt-5 sm:text-lg sm:leading-8">{page.subtitle}</p> : null}
          </div>
          <div className="w-full max-w-full overflow-hidden rounded-lg border border-cyan-300/25 bg-black/40 shadow-neon sm:max-w-md lg:max-w-none">
            {page.profileImageUrl ? (
              <SafeImage src={page.profileImageUrl} alt={page.pageTitle} fallbackLabel="KL" className="aspect-square h-auto w-full max-w-full object-cover" />
            ) : (
              <div className="grid aspect-square place-items-center bg-cyan-300/5 font-display text-6xl font-bold text-cyan-100">KL</div>
            )}
          </div>
        </div>
      </CyberPanel>

      <CyberPanel className="mt-6 sm:mt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200 sm:text-sm sm:tracking-[0.28em]">Biography</p>
        {page.biography ? <MarkdownContent content={page.biography} /> : <p className="mt-4 text-content-secondary">Biography content is being prepared.</p>}
      </CyberPanel>

      {page.specialties.length > 0 ? (
        <section className="mt-6 sm:mt-8">
          <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">Specialties</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3 md:gap-5">
            {page.specialties.map((item) => (
              <CyberPanel key={`${item.title}-${item.sortOrder}`} className="h-full">
                <div className="mb-4 grid size-12 place-items-center rounded-md border border-cyan-300/30 bg-cyan-300/10 font-display text-xl font-bold text-cyan-100">
                  <IconRenderer value={item.icon} />
                </div>
                <h3 className="break-words font-display text-xl font-bold uppercase text-white sm:text-2xl">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-content-secondary">{item.description}</p>
              </CyberPanel>
            ))}
          </div>
        </section>
      ) : null}

      {page.timeline.length > 0 ? (
        <section className="mt-6 sm:mt-8">
          <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">Experience Timeline</h2>
          <div className="mt-5 space-y-4 border-l border-cyan-300/25 pl-5">
            {page.timeline.map((item) => (
              <div key={`${item.year}-${item.title}`} className="relative rounded-md border border-cyan-300/15 bg-black/30 p-4">
                <span className="absolute -left-[1.85rem] top-5 size-3 rounded-full bg-cyan-300 shadow-neon" />
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-100">{item.year}</p>
                <h3 className="mt-2 break-words font-display text-lg font-bold uppercase text-white sm:text-xl">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-content-secondary">{item.description}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {page.faqs.length > 0 ? (
        <section className="mt-6 sm:mt-8">
          <h2 className="font-display text-2xl font-bold uppercase text-white sm:text-3xl">FAQ</h2>
          <div className="mt-4 space-y-3">
            {page.faqs.map((faq, index) => {
              const key = `${faq.question}-${index}`;
              const isOpen = openFaq === key;
              return (
                <div key={key} className="rounded-md border border-cyan-300/15 bg-black/30">
                  <button
                    type="button"
                    className="flex w-full min-w-0 items-center justify-between gap-4 px-4 py-4 text-left font-display text-base font-bold uppercase text-white sm:text-lg"
                    onClick={() => setOpenFaq(isOpen ? null : key)}
                  >
                    <span className="min-w-0 break-words">{faq.question}</span>
                    <span className="text-cyan-100">{isOpen ? "-" : "+"}</span>
                  </button>
                  {isOpen ? <p className="px-4 pb-4 text-sm leading-7 text-content-secondary">{faq.answer}</p> : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {(page.ctaHeadline || page.ctaDescription || page.ctaButtonText) ? (
        <CyberPanel className="mt-6 sm:mt-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200">Next Step</p>
              <h2 className="mt-3 break-words font-display text-2xl font-bold uppercase text-white sm:text-3xl">{page.ctaHeadline || "Start a Project"}</h2>
              {page.ctaDescription ? <p className="mt-3 max-w-2xl text-content-secondary">{page.ctaDescription}</p> : null}
            </div>
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to={ctaUrl}>{page.ctaButtonText || "Contact"}</Link>
            </Button>
          </div>
        </CyberPanel>
      ) : null}
    </section>
  );
}
